import Button from "components/Button";
import Coupon from "components/EntityCoupon";
import Dates from "components/EntityDates";
import Plans from "components/EntityPlans";
import PhoneInputAuth from "components/PhoneInputAuth";
import TextInputAuth from "components/TextInputAuth";
import returnDate from "helpers/handleDate";
import handleErrors from "helpers/handleErrors";
import toastMessage from "helpers/handleToastMessage";
import { Dispatch, useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { checkCoupon } from "services/coupon";
import { createEntity, editEntity, listEntityById } from "services/entity";
import { listPlan } from "services/plan";
import { Plan } from "types/plan";
import { getPlansWithDiscounts } from "utils/getPlansWithDiscount";
import ConfirmWhite from "../../icons/confirm-circle-white.svg";
import { schemaEntityManagement } from "./validation";

interface IFormEntityProps {
  setLoading: Dispatch<React.SetStateAction<boolean>>;
  setLoadingPlans: Dispatch<React.SetStateAction<boolean>>;
  id: string;
  setId: Dispatch<React.SetStateAction<string>>;
  name: string;
  setName: Dispatch<React.SetStateAction<string>>;
}
export const FormEntity = ({
  setLoading,
  setLoadingPlans,
  id,
  setId,
  name,
  setName,
}: IFormEntityProps) => {
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [email, setEmail] = useState("");
  const [startContract, setStartContract] = useState("");
  const [endContract, setEndContract] = useState("");
  const [errors, setErrors] = useState<any>();
  const [couponCode, setCouponCode] = useState("");
  const [isValidCoupon, setIsValidCoupon] = useState<boolean>(false);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [couponData, setCouponData] = useState<any>();
  const [originalValueMonth, setOriginalValueMonth] = useState<number>(0);
  const [originalValueOneTime, setOriginalValueOneTime] = useState<number>(0);

  let history = useHistory();

  const listEntity = async (id: string) => {
    try {
      setLoading(true);
      const { data } = await listEntityById(id);
      setName(data.name);
      setPhone(data.phone);
      setAddress(data.address);
      setEmail(data.email);
      returnDate(data.startContract);
      setStartContract(returnDate(data.startContract));
      setEndContract(returnDate(data.endContract));
      setCouponCode(data?.coupon || "");
      await validCoupon(data?.coupon || "");
    } catch (error: any) {
      toastMessage({
        message: "Error, Something went wrong.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const pathnameId = history.location.pathname.split("/")[2];
    if (pathnameId !== "create") {
      setId(history.location.pathname.split("/")[2]);
    }

    if (id && plans) {
      listEntity(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, plans?.length]);

  const getPlansList = useCallback(async () => {
    try {
      setLoadingPlans(true);
      let plansWithDiscount = [];
      const { data } = await listPlan();
      if (data.Plans.length > 0) {
        plansWithDiscount = data.Plans.filter(
          (plan: any) =>
            plan.active === true && plan.id !== "price_1PvH7gBYagSIczaot9LJtvaP"
        );
      }

      plansWithDiscount = await getPlansWithDiscounts({
        coupon: "",
        currentPlans: plansWithDiscount,
      });

      plansWithDiscount.forEach((plan: Plan) => {
        if (plan.type === "one_time") {
          setOriginalValueOneTime(plan.unit_amount);
        }
        if (plan.type === "recurring") {
          setOriginalValueMonth(plan.unit_amount);
        }
      });

      setPlans(plansWithDiscount);
    } catch (error: any) {
      toastMessage({
        message: "Error, Something went wrong.",
        type: "error",
      });
    } finally {
      setLoadingPlans(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getPlansList();
  }, [getPlansList]);

  const addClass = () => {
    if (name && phone && address && email && startContract && endContract) {
      return "bg-bt-green";
    }

    return "bg-bt-gray-500";
  };

  const validCoupon = async (sendCoupon: string) => {
    try {
      setLoading(true);
      if (couponData?.id === sendCoupon) {
        return;
      }

      const { data: dataCoupon } = await checkCoupon(sendCoupon);

      const plansWithDiscount = await getPlansWithDiscounts({
        coupon: sendCoupon,
        currentPlans: plans,
        originalValueMonth,
        originalValueOneTime,
      });

      setPlans(plansWithDiscount);
      setCouponData(dataCoupon);
      setIsValidCoupon(true);
    } catch (_) {
      await getPlansList();
      setCouponCode("");
      setCouponData("COUPON NOT VALID!");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      if (endContract.length !== 10) {
        setErrors({
          endContract: "End contract field is wrong",
        });
        setLoading(false);
        return;
      }

      if (startContract.length !== 10) {
        setErrors({
          startContract: "Start contract field is wrong",
        });
        setLoading(false);
        return;
      }

      if (startContract >= endContract) {
        setErrors({
          endContract: "End contract must be greater than start contract",
          startContract: "Start contract must be less than end contract",
        });
        setLoading(false);
        return;
      }

      if (
        !schemaEntityManagement.isValidSync({
          name,
          phone,
          address,
          email,
          startContract,
          endContract,
        })
      ) {
        handleErrors({
          schema: schemaEntityManagement,
          stateData: {
            name,
            phone,
            address,
            email,
            startContract,
            endContract,
          },
          setErrorMessage: setErrors,
        });
        setLoading(false);
        return;
      }

      const entityBody = {
        name,
        phone,
        address,
        email,
        startContract,
        endContract,
        subtype: "agency",
        coupon: couponCode,
      };

      if (id) {
        await editEntity(entityBody, id);
        toastMessage({
          message: "Edit successfully.",
          type: "success",
        });
      } else {
        await createEntity(entityBody);
      }

      history.push("/entity-management");
    } catch (error: any) {
      let message;
      if (error.response.data.error.keyPattern.email === 1) {
        message = "This email is already in our database.";
        setErrors({ email: "This email is already in our database." });
      }
      toastMessage({
        message: message ? message : "Error, Something went wrong.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-bt-white p-6">
      <div className="flex flex-row p-2">
        <div className="flex flex-col w-1/2 mr-2">
          <TextInputAuth
            placeholder={"Entity name"}
            label="Entity name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            isHelp={errors?.name}
            helpText={errors?.name}
          />
        </div>
        <div className="flex flex-col w-1/2 ml-2">
          <PhoneInputAuth
            placeholder={"Phone"}
            label="Phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            isHelp={errors?.phone}
            helpText={errors?.phone}
          />
        </div>
      </div>
      <div className="flex flex-row p-2">
        <div className="flex flex-col w-full">
          <TextInputAuth
            placeholder={"Address"}
            label="Address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            isHelp={errors?.address}
            helpText={errors?.address}
          />
        </div>
      </div>
      <div className="flex flex-row p-2">
        <div className="flex flex-col w-1/2">
          <TextInputAuth
            placeholder={"Email"}
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            isHelp={errors?.email}
            helpText={errors?.email}
          />
        </div>
        <div className="flex flex-col w-1/2">
          <Plans plans={plans} />
        </div>
      </div>
      <div className="flex flex-row p-2">
        <div className="flex flex-col w-1/2">
          <Dates
            errors={errors}
            startContract={startContract}
            setStartContract={setStartContract}
            endContract={endContract}
            setEndContract={setEndContract}
          />
        </div>

        <div className="flex flex-col w-1/2">
          <Coupon
            couponCode={couponCode}
            setCouponCode={setCouponCode}
            validCoupon={validCoupon}
            isValidCoupon={isValidCoupon}
            couponData={couponData}
          />
        </div>
      </div>
      <div className="flex flex-row justify-end mt-5 p-2">
        <Button
          label={id ? "Save Changes" : "Create Account"}
          className={`h-14 w-44 ${addClass()} text-base text-white rounded-full font-sora font-medium2`}
          iconLeft={ConfirmWhite}
          onClick={handleSubmit}
        />
      </div>
    </div>
  );
};
